import type { PrimaryDestinationType } from '@/types/city';
import type {
  SocialImportDestination,
  SocialImportSavesDestination,
} from '@/utils/inspoImportBuilder';

export type ItineraryImportSourceType = 'text' | 'pdf' | 'docx' | 'instagram' | 'tiktok';

export interface ItineraryImportSocialAttribution {
  post_id: string;
  short_code?: string;
  platform?: 'instagram' | 'tiktok';
  shared_url?: string;
  display_url?: string | null;
  owner_username?: string | null;
}

export type ItineraryImportMatchConfidence = 'high' | 'low' | 'none';

export type ItineraryImportGtCategory = 'eat' | 'stay' | 'do';

export type ItineraryImportSuggestedResultType = 'itinerary' | 'list';

export type ItineraryImportErrorCode =
  | 'INVALID_SOURCE'
  | 'INVALID_FILE'
  | 'TEXT_TOO_LONG'
  | 'INSUFFICIENT_TEXT'
  | 'EXTRACTION_FAILED'
  | 'RATE_LIMITED'
  | 'UPSTREAM_FAILED'
  | 'INTERNAL_ERROR'
  | 'NO_PLACES_FOUND';

export interface ItineraryImportDestination {
  type: PrimaryDestinationType;
  identifying_name: string;
  google_place_id?: string;
  geoposition?: { type: 'Point'; coordinates: [number, number] };
}

export interface ItineraryImportLocation {
  _id: string;
  google_place_id?: string;
  name?: string;
  address?: string;
  geoposition?: { type: 'Point'; coordinates: [number, number] };
  photos?: string[];
  gt_category?: ItineraryImportGtCategory;
}

export type ItineraryImportRowKind = 'place' | 'travel';

export interface ItineraryImportDraftPlace {
  client_id: string;
  name: string;
  notes?: string | null;
  time?: string | null;
  source_quote?: string | null;
  row_kind?: ItineraryImportRowKind;
  category?: ItineraryImportGtCategory | null;
  match_confidence: ItineraryImportMatchConfidence;
  match_reason?: string | null;
  location?: ItineraryImportLocation | null;
}

export interface ItineraryImportDraftDay {
  day_index: number;
  label?: string | null;
  date?: string | null;
  notes?: string | null;
  places: ItineraryImportDraftPlace[];
}

export interface ItineraryImportRedactions {
  emails: number;
  phones: number;
  prices: number;
  names: number;
  booking_refs: number;
}

export interface ItineraryImportDraft {
  title: string;
  summary?: string | null;
  destination?: ItineraryImportDestination | null;
  day_count: number;
  start_date_string?: string | null;
  end_date_string?: string | null;
  days: ItineraryImportDraftDay[];
  redactions?: ItineraryImportRedactions;
  from_cache?: boolean;
  content_hash?: string;
  prompt_version?: string;
  import_id?: string;
  import_started_at?: string;
  suggested_result_type?: ItineraryImportSuggestedResultType;
  suggestion_reason?: string | null;
  /** Present when source is instagram/tiktok; not part of LLM schema. */
  social?: ItineraryImportSocialAttribution;
}

export function isSocialItineraryImportSourceType(
  sourceType: ItineraryImportSourceType,
): sourceType is 'instagram' | 'tiktok' {
  return sourceType === 'instagram' || sourceType === 'tiktok';
}

export interface ItineraryImportErrorBody {
  status: 'error';
  code: ItineraryImportErrorCode;
  message: string;
  retryable?: boolean;
}

export type TripImportEntry =
  | 'start_building'
  | 'create_flyout'
  | 'mobile_create_sheet'
  | 'mobile_nav'
  | 'onboarding'
  | 'inspo_import';

export type TripImportResultType = 'itinerary' | 'list';

export type TripImportSaveSummary =
  | {
      resultType: 'itinerary';
      destination: SocialImportDestination;
      postId: string;
      title: string;
    }
  | {
      resultType: 'list';
      destination: SocialImportDestination | SocialImportSavesDestination;
      savedCount: number;
      selectedCount: number;
      previewPlaceTitle?: string | null;
    };

export const IMPORT_TEXT_MAX_CHARS = 40_000;
export const IMPORT_FILE_MAX_BYTES = 20 * 1024 * 1024;
/** Backend rejects PDFs over this page count (`INVALID_FILE`). */
export const IMPORT_PDF_MAX_PAGES = 50;
export const IMPORT_REQUEST_TIMEOUT_MS = 55_000;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isItineraryImportDraft(value: unknown): value is ItineraryImportDraft {
  if (!isRecord(value)) return false;
  if (typeof value.title !== 'string') return false;
  if (!Array.isArray(value.days)) return false;
  if (typeof value.day_count !== 'number') return false;
  return value.days.every((day) => {
    if (!isRecord(day)) return false;
    if (typeof day.day_index !== 'number') return false;
    if (!Array.isArray(day.places)) return false;
    return day.places.every((place) => isRecord(place) && typeof place.client_id === 'string');
  });
}

export function isItineraryImportErrorBody(value: unknown): value is ItineraryImportErrorBody {
  if (!isRecord(value)) return false;
  return value.status === 'error' && typeof value.code === 'string' && typeof value.message === 'string';
}

export function inferSuggestedResultType(draft: ItineraryImportDraft): ItineraryImportSuggestedResultType {
  const daysWithPlaces = draft.days.filter((day) => day.places.length > 0);
  if (daysWithPlaces.length >= 2) {
    return 'itinerary';
  }
  const firstDay = draft.days[0];
  const label = firstDay?.label?.trim() ?? '';
  const hasExplicitDayStructure =
    daysWithPlaces.length === 1 &&
    (Boolean(firstDay?.date) || /^day\s+\d+/i.test(label) || label.includes('—') || label.includes('-'));
  if (hasExplicitDayStructure) {
    return 'itinerary';
  }
  return 'list';
}

export function resolveSuggestedResultType(draft: ItineraryImportDraft): {
  type: ItineraryImportSuggestedResultType;
  reason: string | null;
} {
  if (draft.suggested_result_type) {
    return {
      type: draft.suggested_result_type,
      reason: draft.suggestion_reason ?? null,
    };
  }
  const inferred = inferSuggestedResultType(draft);
  return {
    type: inferred,
    reason:
      inferred === 'list'
        ? 'No day-by-day structure found'
        : 'Day-by-day structure detected',
  };
}
